<!-- eslint-disable sonarjs/no-identical-expressions -->
<script setup lang="ts">
import {useApi} from '@/ts/composables/stateful/use-api';
import InterventionShort from '@/vue/organisms/intervention-short.vue';
import {PublicationState} from '@/ts/types/dto/interventions-dto';
import {useLoadingDataRefNull} from '@/ts/composables/pure/use-loading-data-ref';
import {computed, onMounted, ref, watch} from 'vue';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import LoadingError from '@/vue/templates/loading-error.vue';
import {useRouter} from 'vue-router';
import BalmDialog from '@/vue/atoms/balm-dialog.vue';
import {InterventionRouteName, pagesPath} from '@/ts/router/pages-path';
import {MetricAction, MetricCategory} from '@/ts/types/dto/metric-dto';
import MedicalDeviceConsentModal from '@/vue/molecules/medical-device-consent-modal.vue';
import {useUserStore} from '@/ts/store/user-store';
import {AppError} from '@/ts/utils/errors';
import type {ConsentApprovalRequest} from '@/ts/types/dto/user-dto';
import {ErrorCodesDto} from '@/ts/types/dto/error-codes-dto';


const SORTING_ORDER = [
  PublicationState.COMPLETED,
  PublicationState.IN_PROGRESS,
  PublicationState.NEW,
  PublicationState.ACTIVE,
  PublicationState.PREVIEW,
  PublicationState.MAINTENANCE,
  PublicationState.DISCONTINUED,
  PublicationState.WITHDREW,
  PublicationState.NOT_JOINED,
];

const router = useRouter();
const api = useApi();
const userStore = useUserStore();

// Medical device consent state
const showMedicalDeviceConsentModal = ref(false);
const consentError = ref(false);

const needsMedicalDeviceConsent = computed(() => {
  const consent = userStore.userData?.consents?.medicalDeviceConsent;
  // Show dialog if:
  // - medicalDeviceConsent is null/undefined (no consent record)
  // - OR approved is false (consent declined)
  // - OR needsReapproval is true (version changed, needs re-consent)
  return !consent || !consent.approved || consent.needsReapproval;
});

// Fetch interventions - handle "consent_needs_reapproval" error
async function fetchInterventions(): ReturnType<typeof api.me.getInterventions> {
  try {
    return await api.me.getInterventions();
  } catch (err) {
    // Check if error is due to missing consent
    if (err instanceof AppError && err.statusCode === ErrorCodesDto.CONSENT_NEEDS_REAPPROVAL) {
      consentError.value = true;
      showMedicalDeviceConsentModal.value = true;
      return null as unknown as Awaited<ReturnType<typeof api.me.getInterventions>>;
    }
    throw err;
  }
}

const [data, loading, error] = useLoadingDataRefNull(fetchInterventions);

// Track page view and check consent from user store
onMounted(() => {
  void api.metric.logEvent({
    category: MetricCategory.PathsWebPage,
    action: MetricAction.View,
  });

  // Check if user needs to accept medical device consent based on user store
  if (needsMedicalDeviceConsent.value) {
    showMedicalDeviceConsentModal.value = true;
  }
});

// Watch for consent error changes to show modal
watch(consentError, (hasError) => {
  if (hasError) {
    showMedicalDeviceConsentModal.value = true;
  }
});

async function handleMedicalDeviceConsentAccepted(): Promise<void> {
  // Refresh user data and interventions after consent is accepted
  try {
    const user = await api.me.getMe();
    userStore.setUserData(user);

    // Refetch interventions now that consent is granted
    consentError.value = false;
    data.value = await api.me.getInterventions();
  } catch {
    // Consent was already saved, just close the modal
  }
}

async function handleMedicalDeviceConsentClosed(): Promise<void> {
  // User dismissed the modal via X button - set consent to false
  try {
    const request: ConsentApprovalRequest = {
      consentType: 'medical_device',
      approved: false,
    };
    await api.me.putConsentApproval(request);

    // Refresh user data to reflect the declined consent
    const user = await api.me.getMe();
    userStore.setUserData(user);
  } catch {
    // Error setting consent to false, but we still close the modal
  }
}

const interventions = computed(() => {
  if (data.value) {
    const unsorted = data.value;
    return unsorted.sort((aa,bb) => SORTING_ORDER.indexOf(aa.publicationState) - SORTING_ORDER.indexOf(bb.publicationState));
  }
  return [];
});

const showInterventionDetails = computed(() => 
  router.currentRoute.value.matched.some(route => route.name === InterventionRouteName.interventionInfo));

function closeModal(): void {
  // mount lifecycle doesn't trigger onOpen, so no value check required
  void router.push(pagesPath.intervention.interventionsList);
}

</script>

<template>
  <div class="page">
    <!-- Skeleton loading state -->
    <template v-if="loading">
      <div class="paths-header">
        <q-skeleton type="text" width="120px" height="40px"/>
      </div>
      <div class="list">
        <div v-for="n in 5" :key="n" class="skeleton-card">
          <div class="skeleton-image-space"/>
          <div class="skeleton-content">
            <q-skeleton type="text" width="70%" height="28px"/>
            <q-skeleton type="text" width="100%"/>
            <q-skeleton type="text" width="100%"/>
            <q-skeleton type="text" width="60%"/>
          </div>
          <div class="skeleton-footer">
            <q-skeleton type="QBtn" height="48px"/>
          </div>
        </div>
      </div>
    </template>

    <!-- Error state -->
    <loading-error
      v-else-if="error"
      id="intevention-list"
      :loading="false"
      type="page"
      :error="error"
    />

    <!-- Content -->
    <template v-else>
      <div class="paths-header">
        <h1 class="title">
          {{ $t('programs') }}
        </h1>
        <svg-illustration class="arrow-right" name="right-down-arrow.svg" inner-class="inner-right"/>
      </div>
      <div class="list">
        <intervention-short
          v-for="intervention in interventions"
          :key="intervention.id"
          :intervention="intervention"
        />
      </div>

      <!-- Medical Device Disclaimer -->
      <div class="medical-disclaimer">
        {{ $t('medical_device_disclaimer') }}
      </div>

      <balm-dialog :model-value="showInterventionDetails" theme="grey-10" no-padding @update:model-value="closeModal">
        <router-view name="modal"/>
      </balm-dialog>
    </template>

    <!-- Medical Device Consent Modal -->
    <medical-device-consent-modal
      v-model="showMedicalDeviceConsentModal"
      @accepted="handleMedicalDeviceConsentAccepted"
      @close="handleMedicalDeviceConsentClosed"
    />
  </div>
</template>
<style lang="scss" scoped>
.list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--Spacing-spacing-4);
  height: 100%;

  @include screen-md {
    grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  }

  @include screen-lg {
    grid-template-columns: repeat(auto-fit, minmax(367px, 1fr));
  }
}

.paths-header {
  display: flex;
  align-items: center;
  padding-bottom: var(--Spacing-spacing-4);
  gap: var(--Spacing-spacing-2);
}

.title {
  @include font-h1-bold;
}


.page {
  @include page-alignment;
  margin-bottom: var(--Spacing-spacing-10);
}

.medical-disclaimer {
  @include font-body-sm;
  color: var(--Neutral-UI-neutral-ui-80);
  text-align: center;
  margin-bottom: var(--Spacing-spacing-4);
  padding: var(--Spacing-spacing-3);
}

.skeleton-card {
  background-color: var(--Neutral-UI-neutral-ui-20);
  border-radius: var(--card-border-radius);
  min-height: 400px;
  display: flex;
  flex-direction: column;

  @include screen-md {
    min-height: 520px;
  }

  @include screen-lg {
    min-height: 700px;
  }
}

.skeleton-image-space {
  height: 200px;
}

.skeleton-content {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-2);
  padding: var(--Spacing-spacing-6) var(--Spacing-spacing-4) var(--Spacing-spacing-4);

  @include screen-lg {
    padding: var(--Spacing-spacing-8) var(--Spacing-spacing-6) var(--Spacing-spacing-6);
  }
}

.skeleton-footer {
  margin-top: auto;
  padding: 0 var(--Spacing-spacing-4) var(--Spacing-spacing-4);

  @include screen-lg {
    padding: 0 var(--Spacing-spacing-6) var(--Spacing-spacing-6);
  }

  :deep(.q-skeleton) {
    width: 100%;
    border-radius: var(--button-border-radius);
  }
}
</style>
